import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { compose } from "recompose";
import { withStyles, Paper, Typography, IconButton, Select, MenuItem, Button } from "@material-ui/core";
import SingleOption from "./SingleOption";
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import t from "../../../../languages/pl";
import { notArrayOrEmpty } from '../../../../utils/helpers';
import guid from '../../../../utils/guid';
import { TooltipRight } from "../../../Common/CustomTooltips";
import withSaveButton from './../../../HOC/withSaveButton';
import TrophyOption from './TrophyOption';
import ExpectedFuelConsumptionDialog from './ExpectedFuelConsumptionDialog';


const useStyles = theme => ({
    paper: {
        margin: theme.spacing(1),
        padding: theme.spacing(2)
    },
    content: {
        margin: theme.spacing(1),
        padding: theme.spacing(1)
    },
    row: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        margin: theme.spacing(1)
    },
    select: {
        minWidth: 80,
        marginRight: theme.spacing(1)
    }
});

const poolDivideOptions = [10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60];
const timeBeforeSendingOfflineNotificationOptions = [1, 2, 3, 6, 12, 24, 72, 168];

const transformHoursToHumanReadableTime = hours => {
    if (hours === 24)
        return `1 ${t.day}`;
    else if (hours > 24)
        return `${Math.floor(hours / 24)} ${t.days}`;
    return `${hours} godz.`;
};

const OtherSettings = props => {
    const { classes, data, onChange, trophySettings, onConfirmTrophySettings, onExpectedFuelConsumptionSubmit } = props;
    const [internalState, setInternalState] = useState(data);
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    useEffect(() => {
        setInternalState(data);
        props.onFormDataChange(data);
    }, [data]);

    useEffect(() => {
        props.onFormDataChange(internalState);
        onChange && onChange(internalState);
    }, [internalState]);

    const printOptions = options => {
        if (notArrayOrEmpty(options))
            return;
        return options.map(option => (
            <MenuItem key={guid()} value={option}>{option}</MenuItem>
        ));
    };

    const printOptions2 = options => {
        if (notArrayOrEmpty(options))
            return;
        return options.map(option => (
            <MenuItem key={guid()} value={option}>{transformHoursToHumanReadableTime(option)}</MenuItem>
        ));
    };

    const handleChange = (value, name) => {
        setInternalState(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleExpectedFuelConsumptionSubmit = (data) => {
        onExpectedFuelConsumptionSubmit(data);
        setIsDialogOpen(false);
    };

    return (
        <Paper
            square
            className={classes.paper}
        >
            <Typography variant="subtitle1">
                {t.others}
            </Typography>
            <div className={classes.content}>
                <SingleOption
                    title={t.turnOnAutomaticContractNumeration}
                    checked={internalState.auto_nr_umowy}
                    onChange={(value) => handleChange(value, "auto_nr_umowy")}
                />
                <SingleOption
                    title={t.shareClientsWithSubordinateUser}
                    checked={internalState.wspolni_klienci}
                    onChange={(value) => handleChange(value, "wspolni_klienci")}
                />
                <SingleOption
                    title={t.rentRequiresServiceOrder}
                    checked={internalState.wymagany_nr_zlecenia}
                    onChange={(value) => handleChange(value, "wymagany_nr_zlecenia")}
                />
                <SingleOption
                    title={t.showZeroDistance}
                    checked={internalState.ewidencja_zerowe_dyst}
                    onChange={(value) => handleChange(value, "ewidencja_zerowe_dyst")}
                />
                <SingleOption
                    title={t.showAdditionalColumnsInReportRecord}
                    checked={internalState.ewidencja_dodatkowe_kol}
                    onChange={(value) => handleChange(value, "ewidencja_dodatkowe_kol")}
                >
                    <TooltipRight title={t.showAdditionalColumnsInReportRecordTooltip}>
                        <IconButton size="small">
                            <HelpOutlineIcon />
                        </IconButton>
                    </TooltipRight>
                </SingleOption>
                <TrophyOption
                    isCollectingTrophies={trophySettings}
                    onConfirmClick={onConfirmTrophySettings}
                />
                <SingleOption
                    title={t.automaticDivisionOfPools}
                    checked={internalState.auto_dzielenie_pool}
                    onChange={(value) => handleChange(value, "auto_dzielenie_pool")}
                />
                <div className={classes.row}>
                    <Typography variant="body1">
                        {t.poolDivideInterval}
                    </Typography>
                    <Select
                        className={classes.select}
                        defaultValue=""
                        disabled={!internalState.auto_dzielenie_pool}
                        value={internalState.auto_dzielenie_pool_czas === 0 ? "" : internalState.auto_dzielenie_pool_czas}
                        onChange={(e) => handleChange(e.target.value, "auto_dzielenie_pool_czas")}
                    >
                        <MenuItem value="">
                            <em>{t.pick}</em>
                        </MenuItem>
                        {printOptions(poolDivideOptions)}
                    </Select>
                </div>
                <div className={classes.row}>
                    <Typography variant="body1">
                        {t.timeBeforeSendingOfflineNotification1}
                        <br />
                        {t.timeBeforeSendingOfflineNotification2}
                    </Typography>
                    <Select
                        className={classes.select}
                        defaultValue=""
                        value={internalState.czas_rozlaczen_pow}
                        onChange={(e) => handleChange(e.target.value, "czas_rozlaczen_pow")}
                    >
                        <MenuItem value="">
                            <em>{t.pick}</em>
                        </MenuItem>
                        {printOptions2(timeBeforeSendingOfflineNotificationOptions)}
                    </Select>
                </div>
                <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    className={classes.button}
                    onClick={() => setIsDialogOpen(true)}
                >
                    {t.setExpectedFuelConsumption}
                </Button>
            </div>
            <ExpectedFuelConsumptionDialog
                open={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                onExpectedFuelConsumptionSubmit={handleExpectedFuelConsumptionSubmit}
            />
        </Paper>
    );
};

OtherSettings.propTypes = {
    classes: PropTypes.object,
    onChange: PropTypes.func,
    trophySettings: PropTypes.bool,
    onConfirmTrophySettings: PropTypes.func,
    onExpectedFuelConsumptionSubmit: PropTypes.func,
    //redux
    data: PropTypes.object,
    //withSaveButton
    onFormDataChange: PropTypes.func
};

export default compose(
    withStyles(useStyles),
    withSaveButton
)(OtherSettings);
